import { useForm } from 'react-hook-form'

import { format } from 'date-fns'
import { z } from 'zod';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Botao,
  Card,
  Cores,
  Corpo,
  FormularioCabecalho,
  FormularioGrid,
  FormularioLabel,
  FormularioRodape,
  FormularioSectionTitle,
  Textfield,
  Titulo,
} from '@componentes-mu/componentes-mu'

import styles from './style.module.scss'
import { TicketSupportForm } from '../../types/atendimentos/pre-atendimento/types'
import { InputTextArea } from '../../components/InputTextArea/InputTextArea'
import { Contact } from '../../types/cliente/contatos/types'
import { Select, SelectOption } from '../../components/Select'
import { UserDetailsApiResponse } from '../../types/user/types'
import { InputFile } from '../../components/InputFile';

export type CadastrarTicketProps = {
  form: TicketSupportForm
  contacts: Contact[]
  user: UserDetailsApiResponse
  files: File[];
  onClearAllFiles?: () => void;
  onFilesChange: (files: File[]) => void;
  onCreate: (data: TicketSupportForm) => void
  onCleanFields: () => void
  onRemoveFile: (index: number) => void;
}

const schema = z.object({
  descricao: z.string().trim().min(1, 'Descrição é obrigatória'),
  versao: z.string().min(1, 'Versão é obrigatória'),
  oidContato: z.number().gt(0, 'Contato é obrigatório'),
  oidresponsavel: z.number(),
  oidCliente: z.number(),
  oidSistema: z.number(),
  oidOpcao: z.number(),
  criadoPorUsuarioMaster: z.number(),
});

const CadastrarTicket = ({ form, user, contacts, files, onFilesChange, onCreate, onCleanFields, onClearAllFiles, onRemoveFile }: CadastrarTicketProps) => {

  const { setValue, watch, handleSubmit, formState: { errors }, reset } = useForm<TicketSupportForm>({
    defaultValues: {
      ...form,
    },
    resolver: zodResolver(schema),
  });

  const getContactsOptions = (contacts: Contact[]): SelectOption[] => [
    { value: '', label: 'Selecione...' },
    ...contacts.map((contact) => ({
      value: contact.oid,
      label: contact.nome,
    })),
  ];

  const contactsOptions = getContactsOptions(contacts)

  return (
    <>
      <form onSubmit={handleSubmit(onCreate)} className={styles.formContainer}>
        <Card
          style={{ padding: '4px 0px 4px 0px', rowGap: '0' }}
          corDeFundo={Cores.GradacaoNeutros.CinzaFillMU200}
          titulo={
            <FormularioCabecalho>
              <Titulo tipo="cabecalho3" negrito={true} cor={Cores.CoresIdentidadeMU.cinzaMu}>
                Iniciar Chamado para Suporte Técnico
              </Titulo>
              <Corpo tipo="overline" negrito={400} cor={Cores.GradacaoPrimaria.AzulMU500}>
                Dúvidas, configurações ou problemas
              </Corpo>
            </FormularioCabecalho>
          }
          corpo={
            <>
              <FormularioGrid tipo="container">
                <FormularioSectionTitle style={{ backgroundColor: Cores.GradacaoNeutros.CinzaFillMU200 }}>
                  <Corpo tipo="label3" negrito={700} cor={Cores.GradacaoNeutros.CinzaFillMU600}>
                    DADOS DO SOLICITANTE
                  </Corpo>
                </FormularioSectionTitle>
                <FormularioGrid
                  tipo="row"
                  style={{ backgroundColor: Cores.GradacaoNeutros.CinzaFillMU100 }}
                >
                  <FormularioGrid tipo="column" width={57}>
                    <FormularioLabel
                      cssLayout="FULL_WIDTH"
                      conteudo={
                        <Corpo tipo="body2" negrito={500}>
                          Cliente:
                        </Corpo>
                      }
                    >
                      <Corpo tipo="body2" negrito={500} cor={Cores.GradacaoNeutros.CinzaFillMU500}>
                        {user.nomeCliente}
                      </Corpo>

                    </FormularioLabel>
                  </FormularioGrid>
                  <FormularioGrid tipo="column" width={43}>
                    <FormularioLabel
                      cssLayout="FULL_WIDTH"
                      conteudo={
                        <Corpo tipo="body2" negrito={500}>
                          Data:
                        </Corpo>
                      }
                    >
                      <Corpo tipo="body2" negrito={500} cor={Cores.GradacaoNeutros.CinzaFillMU500}>
                        {format(new Date(), 'dd/MM/yyyy')}
                      </Corpo>

                    </FormularioLabel>
                  </FormularioGrid>

                </FormularioGrid>
                <FormularioGrid
                  tipo="row"
                  style={{
                    backgroundColor: Cores.GradacaoNeutros.CinzaFillMU100,
                  }}
                >
                  <FormularioGrid tipo="column" width={57}>
                    <FormularioLabel
                      cssLayout="FULL_WIDTH"
                      conteudo={
                        <Corpo tipo="body2" negrito={500}>
                          Nome do Solicitante:
                        </Corpo>
                      }
                    >
                      <>
                        <Select
                          value={watch('oidContato')}
                          onChange={oid => setValue("oidContato", oid ? Number(oid) : 0)}
                          options={contactsOptions}
                          error={Boolean(errors.oidContato)}
                          infoText={errors.oidContato?.message}
                        />
                      </>

                    </FormularioLabel>
                  </FormularioGrid>
                  <FormularioGrid tipo="column" width={43}>
                    <FormularioLabel
                      cssLayout="FULL_WIDTH"
                      conteudo={
                        <Corpo tipo="body2" negrito={500}>
                          Responsável:
                        </Corpo>
                      }
                    >
                      <Corpo tipo="body2" negrito={500} cor={Cores.GradacaoNeutros.CinzaFillMU500}>
                        {user.nomeResponsavel}
                      </Corpo>

                    </FormularioLabel>
                  </FormularioGrid>
                </FormularioGrid>

                <FormularioSectionTitle
                  style={{
                    backgroundColor: Cores.GradacaoNeutros.CinzaFillMU200,
                  }}
                >
                  <Corpo tipo="label3" negrito={700} cor={Cores.GradacaoNeutros.CinzaFillMU500}>
                    SOLICITAÇÃO
                  </Corpo>
                </FormularioSectionTitle>
                <FormularioGrid
                  tipo="row"
                  style={{
                    backgroundColor: Cores.GradacaoNeutros.CinzaFillMU100,
                  }}
                >
                  <FormularioGrid tipo="column" width={35}>
                    <FormularioLabel
                      cssLayout="FULL_WIDTH"
                      conteudo={
                        <Corpo tipo="body2" negrito={500}>
                          Versão:
                        </Corpo>
                      }
                    >
                      <>
                        <Textfield
                          iconeInputField=""
                          titulo=""
                          id="versao"
                          placeholder="X.X.X"
                          tamanho="small"
                          tipoDeEntrada='text'
                          onChange={(e) => setValue("versao", e || '')}
                          valor={watch('versao')}
                          variante={errors.versao ? 'error' : 'regular'}
                          conteudoDeAjuda={{
                            texto: errors.versao?.message || '',
                            estado: errors.versao ? 'error' : 'regular',
                          }}
                          maxLength={20}
                        />
                      </>

                    </FormularioLabel>
                  </FormularioGrid>
                </FormularioGrid>
                <FormularioGrid
                  tipo="row"
                  style={{
                    backgroundColor: Cores.GradacaoNeutros.CinzaFillMU100,
                  }}
                >
                  <FormularioGrid tipo="column" width={100} height="184px">
                    <FormularioLabel
                      cssLayout="FULL_WIDTH"
                      conteudo={
                        <Corpo tipo="body2" negrito={500}>
                          Descreva sua Solicitação:
                        </Corpo>
                      }
                    >
                      <InputTextArea
                        id="descricao"
                        placeholder="Descreva com o máximo de detalhes sua dúvida ou problema encontrado"
                        onChange={(e) => setValue("descricao", e || '')}
                        valor={watch('descricao')}
                        error={Boolean(errors.descricao)}
                        infoText={errors.descricao?.message}
                      />
                    </FormularioLabel>
                  </FormularioGrid>
                </FormularioGrid>
                <FormularioGrid tipo="row">
                  <FormularioGrid
                    tipo="column"
                    width={100}
                    style={{ backgroundColor: Cores.GradacaoNeutros.CinzaFillMU100 }}
                  >
                    <FormularioLabel
                      cssLayout="FULL_WIDTH"
                      conteudo={
                        <Corpo tipo="body2" negrito={500}>
                          Anexo:
                        </Corpo>
                      }
                    >
                      <div style={{ maxWidth: "200px" }}>
                        <InputFile
                          files={files}
                          multiple
                          label="Selecionar arquivos"
                          onFilesChange={onFilesChange}
                          onRemoveFile={onRemoveFile}
                          onClearAll={onClearAllFiles}
                        />
                      </div>

                    </FormularioLabel>
                  </FormularioGrid>
                </FormularioGrid>
              </FormularioGrid>
            </>}
          rodape={
            <FormularioRodape>
              <Botao
                tamanho="medium"
                tipo="tipoDefault"
                estilo="outlined"
                tipoComportamento="button"
                onClick={() => {
                  onCleanFields()
                  reset()
                }}
              >
                Limpar campos
              </Botao>
              <Botao
                tamanho="medium"
                tipo="tipoDefault"
                estilo="primaryDark"
                tipoComportamento="submit"
              >
                Cadastrar
              </Botao>
            </FormularioRodape>
          }
        ></Card>
      </form >
    </>
  )
}

export { CadastrarTicket }
